import type { TFunction } from 'i18next';
import type { StatusTone } from '../../StatusBadge.js';
import type { FormRecordTreeItem, FormRecordTreeView } from '../forms/types.js';
import { revisionShort } from './library.js';

/**
 * ISM ağacında formun kayıtları (ADR 0072): formun altındaki satırlar, kaydın rozeti ve ofiste
 * kayıtların gemisinin bu cihazda hatırlanması. Ağaç bileşeni (`DocumentTree`) bunları çizer.
 */

/** Formun kayıtlarının yükleme durumu (form başına, tembel). */
export type RecordsState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; view: FormRecordTreeView };

/** Formun altındaki satır: kayıt, "All (n)" ya da tek satırlık durum. */
export type RecordChild =
  | { id: string; kind: 'hint' | 'loading' | 'error' | 'empty' }
  | { id: string; kind: 'record'; item: FormRecordTreeItem }
  | { id: string; kind: 'all'; total: number };

/**
 * Açık formun alt satırları. `state === null`: ofiste gemi seçilmedi ("Select a vessel to see
 * records"); `undefined`: henüz istenmedi (yükleniyor sayılır).
 */
export function recordChildren(
  documentId: string,
  state: RecordsState | null | undefined,
): RecordChild[] {
  if (state === null) return [{ id: `${documentId}::hint`, kind: 'hint' }];
  if (!state || state.kind === 'loading')
    return [{ id: `${documentId}::loading`, kind: 'loading' }];
  if (state.kind === 'error') return [{ id: `${documentId}::error`, kind: 'error' }];
  if (state.view.total === 0) return [{ id: `${documentId}::empty`, kind: 'empty' }];
  return [
    ...state.view.items.map((item) => ({
      id: `${documentId}::record::${item.id}`,
      kind: 'record' as const,
      item,
    })),
    { id: `${documentId}::all`, kind: 'all', total: state.view.total },
  ];
}

type T = TFunction | ((key: string, options?: Record<string, unknown>) => string);

export interface RecordTreeBadge {
  tone: StatusTone;
  label: string;
  /** Bekleyen adım: rozet "⏳" ile başlar (ADR 0069 dili). */
  waiting: boolean;
}

const TONES: Readonly<Record<FormRecordTreeItem['status']['status'], StatusTone>> = {
  draft: 'info',
  discarded: 'neutral',
  pending: 'warning',
  returned: 'danger',
  approved: 'success',
  void: 'danger',
  inconsistent: 'danger',
};

/**
 * Kaydın ağaçtaki rozeti (ADR 0072): Draft, Approved, Returned, Void mevcut renk diliyle;
 * bekleyen kayıt "⏳ Waiting for <rol>" (rol adı veriden), rol bilinmiyorsa "Submitted". Süresi
 * geçmiş bekleyen adım kırmızı [MEPC.312(74) 4.3.10].
 */
export function recordTreeBadge(
  t: T,
  item: FormRecordTreeItem,
  roles: Readonly<Record<string, string>>,
): RecordTreeBadge {
  const status = item.status.status;
  if (status === 'pending') {
    // Zincirde bir adım birden çok rütbeyi bekleyebilir (ADR 0071 §4); adı bilinmeyen rol atlanır
    const role = item.pending
      ? (item.pending.approverRoleIds ?? [item.pending.approverRoleId])
          .map((id) => roles[id])
          .filter(Boolean)
          .join(' / ') || undefined
      : undefined;
    return {
      tone: item.overdue ? 'danger' : 'warning',
      label: role
        ? t('documents.tree.records.waitingFor', { role })
        : t('documents.tree.records.submitted'),
      waiting: true,
    };
  }
  return { tone: TONES[status], label: t(`forms.status.${status}`), waiting: false };
}

/**
 * Kayıt satırının kısa revizyonu: kaydın doldurulduğu formun revizyonu, formun satırıyla aynı
 * biçimde ("v2" → "Rev.2"; ADR 0107 §3). Eski sunucu formun revizyonunu vermezse kaydın kendi
 * revizyonu (ADR 0069); hiç gönderilmemiş taslakta yok.
 */
export function recordRevision(item: FormRecordTreeItem): string | null {
  if (item.formRevisionNo !== undefined) return revisionShort(item.formRevisionNo);
  return item.status.revisionNo === null ? null : `Rev.${item.status.revisionNo}`;
}

const VESSEL_KEY = 'felix.documents.recordVessel';

/** Ofiste kayıtların son seçilen gemisi (bu cihazda; depo kapalıysa yok). */
export function recallRecordVessel(): string | null {
  try {
    return globalThis.localStorage?.getItem(VESSEL_KEY) ?? null;
  } catch {
    return null;
  }
}

export function rememberRecordVessel(vesselId: string | null): void {
  try {
    if (vesselId) globalThis.localStorage?.setItem(VESSEL_KEY, vesselId);
    else globalThis.localStorage?.removeItem(VESSEL_KEY);
  } catch {
    // Depo kapalı (gizli pencere): seçim yalnız bu sayfada kalır
  }
}

/** Hatırlanan gemi hâlâ listede mi; değilse seçilmemiş (ipucu görünür). */
export function chooseRecordVessel(
  remembered: string | null,
  vessels: readonly { id: string }[],
): string | null {
  return remembered && vessels.some((v) => v.id === remembered) ? remembered : null;
}
